import React, { useState } from 'react';
import { FaCalculator, FaHandshake, FaInfoCircle } from 'react-icons/fa';
import CurrencyInput from '../../../molecules/CurrencyInput';
import type { ClaimCharges } from '../../../../types/LegalCase';
import {
    chargesRatio,
    chargesRatioClass,
    hasChargeRates,
    hasChargesData,
    isChargesOverridden,
    simulateDiscount,
} from './claimCharges';

// Encargos da pretensão e apoio à negociação (Task 17 / ADR 004).
//
// Mostra os **dois números** que o ADR 004 mantém separados: o saldo (o que se alega) e o valor
// atualizado de hoje (o que se negocia). O card não recalcula nada — o cálculo tem um dono no
// domínio do `recovery`, e repeti-lo aqui criaria duas fontes que divergiriam na primeira mudança
// de regra.

interface ClaimChargesCardProps {
    charges: ClaimCharges | null | undefined;
    // Sugerir o percentual de perda do CPC 25 a partir do desconto simulado (ADR 004). Opcional:
    // quem não passa simplesmente não oferece o atalho. **Sugere, não grava** — quem escreve em
    // campo contábil auditável é o humano, no formulário do caso.
    onSuggestLossPercent?: (percent: number) => void;
}

const Row: React.FC<{ label: string; value: string | null; strong?: boolean; hint?: string }> = ({
    label,
    value,
    strong,
    hint,
}) => (
    <div className="flex items-start justify-between gap-2 text-sm">
        <span className="text-gray-600 dark:text-gray-400 flex items-center gap-1">
            {label}
            {hint && (
                <span title={hint} className="text-gray-400 dark:text-gray-500 cursor-help">
                    <FaInfoCircle size={11} />
                </span>
            )}
        </span>
        <span
            className={
                strong
                    ? 'font-semibold text-gray-900 dark:text-white'
                    : 'text-gray-800 dark:text-gray-200'
            }
        >
            {value ?? '—'}
        </span>
    </div>
);

const formatPercent = (value: number): string =>
    `${value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;

const formatCurrency = (value: number): string =>
    value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const ClaimChargesCard: React.FC<ClaimChargesCardProps> = ({ charges, onSuggestLossPercent }) => {
    const [proposed, setProposed] = useState<number>(0);
    const [simulating, setSimulating] = useState(false);

    if (!hasChargesData(charges)) return null;

    const ratio = chargesRatio(charges);
    const simulation = simulateDiscount(charges.updatedAmount, proposed);

    return (
        <div className="rounded-lg border border-gray-200 dark:border-slate-700 p-3 space-y-2">
            <div className="flex items-center gap-2">
                <FaCalculator className="text-blue-600 dark:text-blue-400" size={12} />
                <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Valor atualizado
                </h4>
            </div>

            {!hasChargeRates(charges) && (
                <p className="text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30 rounded p-2">
                    Sem taxa de juros ou multa cadastrada neste caso — o valor atualizado é igual ao
                    saldo. Informe as taxas ao editar o caso para o sistema calcular os encargos.
                </p>
            )}

            <Row
                label="Saldo a atualizar"
                value={charges.outstandingAmountFormatted}
                hint="O que ainda se deve: base menos o que já foi pago. É sobre este valor que os encargos incidem."
            />
            {charges.paidAmount > 0 && (
                <Row label="Já pago" value={charges.paidAmountFormatted} />
            )}

            <div className="border-t border-gray-200 dark:border-slate-700 my-1" />

            <Row
                label={
                    charges.days === null
                        ? 'Juros de mora'
                        : `Juros de mora (${charges.days} dias${
                              charges.interestPercentFormatted
                                  ? ` · ${charges.interestPercentFormatted}`
                                  : ''
                          })`
                }
                value={charges.interestAmountFormatted}
                hint="1% ao mês pro rata die (art. 406 do CC), contados da entrada do caso no jurídico."
            />
            {charges.interestOverridden && (
                <p className="text-xs text-gray-500 dark:text-gray-400 pl-2">
                    Ajustado à mão. Pela taxa do caso seria{' '}
                    {charges.interestCalculatedFormatted ?? '—'}.
                </p>
            )}

            <Row
                label="Multa"
                value={charges.penaltyAmountFormatted}
                hint="Incide sobre o principal, não sobre principal + juros."
            />
            {charges.penaltyOverridden && (
                <p className="text-xs text-gray-500 dark:text-gray-400 pl-2">
                    Ajustado à mão. Pela taxa do caso seria{' '}
                    {charges.penaltyCalculatedFormatted ?? '—'}.
                </p>
            )}

            {charges.legalCosts > 0 && <Row label="Custas" value={charges.legalCostsFormatted} />}

            <div className="border-t border-gray-200 dark:border-slate-700 my-1" />
            <Row label="Valor atualizado hoje" value={charges.updatedAmountFormatted} strong />

            {ratio !== null && (
                <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-500 dark:text-gray-400">
                        Encargos sobre o saldo
                    </span>
                    <span className={`font-medium ${chargesRatioClass(ratio)}`}>
                        {formatPercent(ratio)}
                    </span>
                </div>
            )}

            {charges.dailyInterestAmount !== null && charges.dailyInterestAmount > 0 && (
                <div className="rounded bg-blue-50 dark:bg-blue-900/30 p-2 flex items-center justify-between">
                    <span className="text-xs text-blue-800 dark:text-blue-300">
                        Cada dia parado acrescenta
                    </span>
                    <span className="text-sm font-semibold text-blue-900 dark:text-blue-200">
                        {charges.dailyInterestAmountFormatted}
                    </span>
                </div>
            )}

            {isChargesOverridden(charges) && (
                <p className="text-xs text-amber-700 dark:text-amber-400">
                    Há encargo ajustado manualmente neste caso — o valor calculado pela regra segue
                    registrado acima.
                </p>
            )}

            <div className="border-t border-gray-200 dark:border-slate-700 pt-2">
                {!simulating && (
                    <button
                        onClick={() => setSimulating(true)}
                        className="flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900 rounded-lg transition-colors"
                    >
                        <FaHandshake size={11} /> Simular acordo
                    </button>
                )}

                {simulating && (
                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <h5 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                Simular acordo
                            </h5>
                            <button
                                onClick={() => {
                                    setSimulating(false);
                                    setProposed(0);
                                }}
                                className="text-xs text-gray-500 dark:text-gray-400 hover:underline"
                            >
                                Fechar
                            </button>
                        </div>
                        <CurrencyInput
                            label="Valor proposto"
                            prefix="R$"
                            fractionDigits={2}
                            value={proposed}
                            onChange={setProposed}
                        />
                        {simulation && proposed > 0 && (
                            <div className="rounded bg-gray-50 dark:bg-slate-900 p-2 space-y-1">
                                <Row
                                    label="Desconto"
                                    value={`${formatCurrency(simulation.discountAmount)} (${formatPercent(
                                        simulation.discountPercent,
                                    )})`}
                                />
                                {simulation.discountAmount < 0 && (
                                    <p className="text-xs text-amber-700 dark:text-amber-400">
                                        A proposta está acima do valor atualizado.
                                    </p>
                                )}
                                {onSuggestLossPercent && simulation.discountAmount > 0 && (
                                    <button
                                        onClick={() =>
                                            onSuggestLossPercent(simulation.suggestedLossPercent)
                                        }
                                        className="text-xs text-blue-600 dark:text-blue-400 hover:underline text-left"
                                    >
                                        Aceitar este desconto equivale a esperar{' '}
                                        {formatPercent(simulation.suggestedLossPercent)} de perda —
                                        usar no risco CPC 25
                                    </button>
                                )}
                            </div>
                        )}
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            Simulação apenas. Para registrar o acordo, use a seção Acordos.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ClaimChargesCard;
